import { InputNumber, InputNumberProps } from 'antd';
import { MeasureType } from 'domains/measurementDomain';
import { useMeasureField, useMeasurementUnit } from 'hooks/useMeasurementUnit';

interface Props
  extends Omit<InputNumberProps, 'value' | 'onChange' | 'formatter' | 'parser'> {
  /** The value as the API returned it, already in the unit the viewer is working in. */
  value?: number | null;
  /** Called with the number the user typed, verbatim - the API converts, not this. */
  onChange?: (value: number | undefined) => void;
  /** Which quantity this field holds, so the right unit and conversion are used. */
  type?: MeasureType;
  /** Set false to leave the unit off the field (e.g. when a `W x L` row labels it once). */
  showSymbol?: boolean;
}

/**
 * A number field that shows and accepts values in the unit the viewer is working in.
 *
 * Nothing is converted on this side. The API serves every measurement in the viewer's unit and
 * stores back whatever it is sent, keeping the entered side exact and deriving the other one.
 * Converting here as well is what turned an entered 888mm into 888.01mm.
 *
 * The field is only ever the *display* of the form's value - the form itself holds
 * throughout. Two consequences matter:
 *
 * - An untouched field never calls `onChange`, so it submits the byte-identical value it loaded.
 *   Round-tripping every field through a 2-decimal metric display and back would quietly rewrite
 *   the whole record on every save.
 * - An edited field is converted back at full precision, never at display precision. 210mm is
 *   stored as 8.267716535433072", not as 8.27".
 *
 * While the user is typing, what they typed is shown verbatim rather than the conversion of the
 * conversion, so the caret does not jump and floating-point noise never surfaces.
 */
export const RevxMeasureInput = ({
  value,
  onChange,
  type = MeasureType.LENGTH,
  showSymbol = true,
  ...rest
}: Props) => {
  const { symbol } = useMeasurementUnit();
  const field = useMeasureField(type, { value, onChange });

  return (
    <InputNumber
      {...rest}
      value={field.value as InputNumberProps['value']}
      addonAfter={showSymbol ? symbol(type) : rest.addonAfter}
      onFocus={(e) => {
        field.onFocus();
        rest.onFocus?.(e);
      }}
      onBlur={(e) => {
        field.onBlur();
        rest.onBlur?.(e);
      }}
      onChange={(next) => field.onChange(next as number | null)}
    />
  );
};
